import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment-timezone';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  AppBar,
  Button,
  Checkbox,
  Grid,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import RowDiningRoom from './TimeTableRows/RowDiningRoom';
import RowDriveThru from './TimeTableRows/RowDriveThru';
import RowDigitalDriveThru from './TimeTableRows/RowDigitalDriveThru';
import RowDeliveryTime from './TimeTableRows/RowDeliveryTime';
import RowCatering from './TimeTableRows/RowCatering';
import RowCurbside from './TimeTableRows/RowCurbside';
import RowArrival from './TimeTableRows/RowArrival';
import RowDeparture from './TimeTableRows/RowDeparture';
import RowShowTime from './TimeTableRows/RowShowTime';
import TimeTableFooter from './TimeTableFooter';
import {
  setCloseToAll,
  setOpen24HoursToAll,
  setSelectedRangeDate,
  setSelectedStartDate,
} from './../../redux/actions/newOperationHoursActions';
import { canSeeDeliveryRow } from '../../utils/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import RBIFollowCursorTooltip from '../RBIFollowCursorTooltip/RBIFollowCursorTooltip';
import { useGeneralTooltip } from '../../utils/tooltipUtils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: 'rgb(138, 138, 138)',
    },
    paper: {
      padding: theme.spacing(2),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      height: '60vh',
      overflow: 'auto',
    },
    desktopCheckboxControl: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
    },
  }),
);

// eslint-disable-next-line import/no-anonymous-default-export
export default function TimeTable({ isRange }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();

  const isPLKAutoCalculatedLateNight = (brand, Region, countries) =>
    brand === 'PLK' &&
    (Region === 'NA' ||
      (countries.length > 0 &&
        countries?.every(val => val.name.split(' - ')[0] === 'NA')));
  const { hasEditHoursOfOperation, hasViewMenuItemOperationHours } =
    useSelector((state: RootStateOrAny) => state.UserReducer.thisUser);
  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const { Region, RestaurantId, TimeZone, RestaurantStatus } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );

  const { selectedRestaurantsList: selectedRestaurants } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer,
  );
  const { permissions, userInfo } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );

  const countries = useSelector(
    (state: RootStateOrAny) =>
      state.ReportingUnitsReducer?.reportingUnitsCountries,
  );

  const {
    selectedStartDate,
    selectedEndDate,
    isCalculated,
    open24Hours,
    closeToAll,
    drIsClosed,
    dtIsClosed,
    drIsOpened24Hours,
    dtIsOpened24Hours,
  } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);

  const isRestaurantClosed = ['Closed', 'Temp Closed'].includes(
    RestaurantStatus,
  );

  const isMDSUpAndRunning = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer.isMDSUpAndRunning,
  );
  const { ramWarningBarToggle } = useFlags();

  const disableAll = getDisableAll({
    RestaurantId,
    hasEditHoursOfOperation,
    hasViewMenuItemOperationHours,
    selectedStartDate,
    TimeZone,
    isRestaurantClosed,
    isMDSUpAndRunning,
  });

  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );

  const curbsideService = restaurantServices.find(
    elem => elem.code === 'curbsidePickup',
  );
  const cateringService = restaurantServices.find(
    elem => elem.code === 'catering',
  );
  const driveThruService = restaurantServices.find(
    elem => elem.code === 'driveThru',
  );
  const digitalDriveThruService = restaurantServices.find(
    elem => elem.code === 'digitalDriveThru',
  );

  const setCheckbox24hours = value => {
    dispatch(setOpen24HoursToAll(value, isCalculated));
  };

  const setCheckboxClose = value => {
    dispatch(setCloseToAll(value));
  };

  const tooltipMessage = useGeneralTooltip({
    ramWarningBarToggle,
    isMDSUpAndRunning,
    restaurantId: RestaurantId,
    isRestaurantClosed,
  });

  return (
    <Grid item xs={12} lg={6}>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar variant="dense">
          <Grid container lg={12}>
            <Grid item lg={4} xs={4} spacing={5} style={{ margin: 'auto' }}>
              <Typography>{t('operationHours.timetable')}</Typography>
            </Grid>
            <Grid item lg={2} xs={1} spacing={5} style={{ marginLeft: 0 }}>
              <Grid container lg={12} spacing={5} direction="row">
                <Grid
                  item
                  lg={1}
                  xs={1}
                  className={classes.desktopCheckboxControl}
                >
                  <Typography style={{ padding: 0 }}>
                    <small>{t('app.common.twentyFourHour')}</small>
                  </Typography>
                  <Checkbox
                    style={{ color: 'white', padding: 0 }}
                    disabled={disableAll}
                    checked={
                      driveThruService &&
                      !driveThruService.value &&
                      drIsOpened24Hours === 'Y' &&
                      dtIsOpened24Hours === 'N'
                        ? true
                        : open24Hours
                    }
                    onChange={e => setCheckbox24hours(e.target.checked)}
                  />
                </Grid>
                <Grid
                  item
                  lg={1}
                  xs={1}
                  className={classes.desktopCheckboxControl}
                >
                  <Typography style={{ padding: 0 }}>
                    <small>{t('operationHours.closed')}</small>
                  </Typography>
                  <Checkbox
                    style={{ color: 'white', padding: 0 }}
                    disabled={disableAll}
                    checked={
                      driveThruService &&
                      !driveThruService.value &&
                      drIsClosed === 'Y' &&
                      dtIsClosed === 'N'
                        ? true
                        : closeToAll
                    }
                    onChange={e => setCheckboxClose(e.target.checked)}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item lg={1} xs={3}></Grid>
            <Grid item lg={5} xs={4}>
              {/* <Tooltip title={ tooltipMessage }>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isRestaurantClosed}
                    onClick={openCloseConfirmationModal}
                  >
                    {t('operationHours.reset')}
                  </Button>
                </span>
              </Tooltip> */}
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <RBIFollowCursorTooltip title={tooltipMessage}>
        <Paper className={classes.paper}>
          <RowArrival disableAll={disableAll} restaurantId={RestaurantId} />
          {brand === 'TH' && (Region === 'NA' || !RestaurantId) && (
            <RowDeparture disableAll={disableAll} restaurantId={RestaurantId} />
          )}
          <RowDiningRoom disableAll={disableAll} restaurantId={RestaurantId} />
          {driveThruService && driveThruService.value ? (
            <RowDriveThru disableAll={disableAll} restaurantId={RestaurantId} />
          ) : null}
          {brand === 'BK' &&
          (Region === 'NA' || !RestaurantId) &&
          digitalDriveThruService &&
          digitalDriveThruService.value ? (
            <RowDigitalDriveThru
              disableAll={disableAll}
              restaurantId={RestaurantId}
            />
          ) : null}
          <RowDeliveryTime
            disableAll={
              disableAll || !canSeeDeliveryRow(permissions, userInfo.role?.code)
            }
          />
          {cateringService &&
          cateringService.value &&
          (brand !== 'PLK' ||
            !isPLKAutoCalculatedLateNight(brand, Region, countries)) ? (
            <RowCatering disableAll={disableAll} restaurantId={RestaurantId} />
          ) : null}
          {curbsideService && curbsideService.value ? (
            <RowCurbside disableAll={disableAll} restaurantId={RestaurantId} />
          ) : null}
          {(brand !== 'PLK' ||
            !isPLKAutoCalculatedLateNight(brand, Region, countries)) && (
            <React.Fragment>
              <RowShowTime
                label={t('operationHours.breakfastTime')}
                open="breakfastTimeOpen"
                close="breakfastTimeClose"
              />
              <RowShowTime
                label={t('operationHours.lunchTime')}
                open="lunchTimeOpen"
                close="lunchTimeClose"
              />
              <RowShowTime
                label={t('operationHours.snackTime')}
                open="snackTimeOpen"
                close="snackTimeClose"
              />
              <RowShowTime
                label={t('operationHours.dinnerTime')}
                open="dinnerTimeOpen"
                close="dinnerTimeClose"
              />
              <RowShowTime
                label={t('operationHours.lateNight')}
                open="lateNightTimeOpen"
                close="lateNightTimeClose"
              />
            </React.Fragment>
          )}
        </Paper>
      </RBIFollowCursorTooltip>
      <TimeTableFooter
        isNotPLKLockedDayparts={
          brand !== 'PLK' ||
          !isPLKAutoCalculatedLateNight(brand, Region, countries)
        }
      />
    </Grid>
  );
}

TimeTable.displayName = 'TimeTable';

function getDisableAll({
  RestaurantId,
  hasEditHoursOfOperation,
  hasViewMenuItemOperationHours,
  selectedStartDate,
  TimeZone,
  isRestaurantClosed,
  isMDSUpAndRunning,
}) {
  /**
   * In case of mass upload considering restaurant's timezone as 'Pacific/Midway' (UTC-11).
   * This will ensure to allow update for most of the restaurants since currently we're not fetching timezone of all the restaurants.
   */
  if (!isMDSUpAndRunning) return true;

  const adjustedTimeZone = RestaurantId ? TimeZone || 'UTC' : 'Pacific/Midway';
  const hasPermission = RestaurantId
    ? hasEditHoursOfOperation
    : hasViewMenuItemOperationHours;
  if (
    !hasPermission ||
    !selectedStartDate ||
    (RestaurantId && isRestaurantClosed)
  )
    return true;

  const startOfSelectedDayInRestaurantTimeZone = moment
    .tz(selectedStartDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', adjustedTimeZone)
    .startOf('day');
  const earliestCutoffDayInRestaurantTimezone = moment
    .tz(adjustedTimeZone)
    .startOf('day');

  return startOfSelectedDayInRestaurantTimeZone.isBefore(
    earliestCutoffDayInRestaurantTimezone,
  );
}
